import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import walletService from '../../services/walletService';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import DataTable from '../../components/common/DataTable';
import Button from '../../components/common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet, faPlus, faShieldHalved, faReceipt, faCircleCheck, faCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import Modal from '../../components/common/Modal';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatAmount(amount) {
  if (amount == null) return '—';
  const num = Number(amount);
  const prefix = num >= 0 ? '+' : '';
  return `${prefix}${num.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
}

// ─── Deposit Modal (superadmin only) ──────────────────────────────────────────

function DepositModal({ onClose, onDeposit }) {
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) return;
    setSubmitting(true);
    await onDeposit(Number(amount));
    setSubmitting(false);
    onClose();
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Manual Deposit"
      subtitle="Issue credits directly to user balance"
      maxWidth={420}
    >
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary, #94a3b8)', marginBottom: 6, fontWeight: 500 }}>
            Amount (credits)
          </label>
          <input
            type="number"
            min="1"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md, 10px)',
              border: '1px solid var(--border-hairline, rgba(255, 255, 255, 0.08))',
              background: 'rgba(255, 255, 255, 0.03)',
              color: 'var(--text-primary, #f8fafc)',
              fontFamily: 'var(--font-mono, monospace)',
              fontSize: '1rem',
              boxSizing: 'border-box',
            }}
            autoFocus
          />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" type="submit" loading={submitting} disabled={!amount}>
            Deposit
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Checkout Modal ───────────────────────────────────────────────────────────

function CheckoutModal({ onClose, onCheckout }) {
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) return;
    setSubmitting(true);
    await onCheckout(Number(amount));
    setSubmitting(false);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Add Credits"
      subtitle="Purchase credits securely via Stripe checkout"
      maxWidth={420}
    >
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary, #94a3b8)', marginBottom: 6, fontWeight: 500 }}>
            Amount (₹)
          </label>
          <input
            type="number"
            min="1"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md, 10px)',
              border: '1px solid var(--border-hairline, rgba(255, 255, 255, 0.08))',
              background: 'rgba(255, 255, 255, 0.03)',
              color: 'var(--text-primary, #f8fafc)',
              fontFamily: 'var(--font-mono, monospace)',
              fontSize: '1rem',
              boxSizing: 'border-box',
            }}
            autoFocus
          />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" type="submit" loading={submitting} disabled={!amount}>
            Pay with Stripe
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Wallet Page ──────────────────────────────────────────────────────────────

export default function WalletPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [ledgerCursor, setLedgerCursor] = useState(null);
  const [hasMoreLedger, setHasMoreLedger] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') {
      showToast('Credits added successfully!', 200);
      searchParams.delete('status');
      setSearchParams(searchParams, { replace: true });
    } else if (status === 'cancelled') {
      showToast('Deposit cancelled', 400);
      searchParams.delete('status');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, showToast]);

  const fetchBalance = useCallback(async () => {
    try {
      const res = await walletService.getBalance();
      setBalance(res?.data?.data || res?.data);
    } catch {
      showToast('Failed to load wallet balance', 500);
    }
  }, [showToast]);

  const fetchLedger = useCallback(async (cursor = null) => {
    setLedgerLoading(true);
    try {
      const res = await walletService.getLedger({ limit: 20, cursor: cursor || undefined });
      const data = res?.data?.data || res?.data;
      const items = Array.isArray(data) ? data : (data?.items || (Array.isArray(data?.entries) ? data.entries : []));
      const pagination = data?.pagination;
      if (cursor) {
        setLedger((prev) => [...prev, ...items]);
      } else {
        setLedger(items);
      }
      setLedgerCursor(pagination?.next_cursor || null);
      setHasMoreLedger(pagination?.has_more || false);
    } catch {
      showToast('Failed to load ledger', 500);
    } finally {
      setLedgerLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    let active = true;
    const init = async () => {
      await Promise.allSettled([fetchBalance(), fetchLedger()]);
      if (active) setLoading(false);
    };
    init();
    return () => { active = false; };
  }, [fetchBalance, fetchLedger]);

  const handleDeposit = async (amount) => {
    try {
      await walletService.deposit(amount);
      showToast('Deposit successful', 200);
      fetchBalance();
      fetchLedger();
    } catch {
      showToast('Deposit failed', 500);
    }
  };

  const handleCheckout = async (amount) => {
    try {
      const res = await walletService.createCheckout({ amount });
      const url = res?.data?.data?.checkout_url || res?.data?.checkout_url || res?.data?.data?.url || res?.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        showToast('Checkout session created', 200);
        setShowCheckout(false);
      }
    } catch {
      showToast('Checkout failed', 500);
    }
  };

  const handleVerify = async () => {
    try {
      const res = await walletService.verifyLedger();
      const data = res?.data?.data || res?.data;
      setVerifyResult(data?.valid ?? data?.verified ?? true);
      showToast(data?.valid !== false ? 'Ledger verified ✓' : 'Ledger integrity issue detected', data?.valid !== false ? 200 : 400);
    } catch {
      setVerifyResult(false);
      showToast('Verification failed', 500);
    }
  };

  const ledgerColumns = [
    { key: 'created_at', label: 'Date', render: (v) => formatDate(v) },
    { key: 'tx_type', label: 'Type', render: (v) => (
      <span style={{
        background: '#f3f4f6', padding: '2px 8px', borderRadius: 12,
        fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
      }}>{v}</span>
    )},
    { key: 'amount', label: 'Amount', render: (v) => (
      <span style={{ color: Number(v) >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
        {formatAmount(v)}
      </span>
    )},
    { key: 'description', label: 'Description' },
    { key: 'running_balance', label: 'Balance', render: (v) => v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '—' },
  ];

  return (
    <DashboardLayout>
      <div style={{ padding: '24px 20px' }}>
        {loading ? <p>Loading wallet…</p> : (
          <>
            {/* Balance Card */}
            <div style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: 16, padding: '28px 32px', marginBottom: 24, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
            }}>
              <div>
                <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 4 }}>
                  <FontAwesomeIcon icon={faWallet} style={{ marginRight: 8 }} />
                  Wallet Balance
                </div>
                <div style={{ fontSize: 36, fontWeight: 800 }}>
                  ₹{Number(balance?.balance ?? balance ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {user?.role !== 'superadmin' && (
                  <Button variant="primary" onClick={() => setShowCheckout(true)}>
                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
                    Add Credits
                  </Button>
                )}
                {user?.role === 'superadmin' && (
                  <Button variant="secondary" onClick={() => setShowDeposit(true)}>
                    <FontAwesomeIcon icon={faReceipt} style={{ marginRight: 6 }} />
                    Manual Deposit
                  </Button>
                )}
                <Button variant="secondary" onClick={handleVerify}>
                  <FontAwesomeIcon icon={faShieldHalved} style={{ marginRight: 6 }} />
                  Verify
                  {verifyResult !== null && (
                    <FontAwesomeIcon
                      icon={verifyResult ? faCircleCheck : faCircleXmark}
                      style={{ marginLeft: 6, color: verifyResult ? '#10b981' : '#ef4444' }}
                    />
                  )}
                </Button>
              </div>
            </div>

            {/* Ledger */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <FontAwesomeIcon icon={faReceipt} style={{ fontSize: 18, color: '#2d8cf0' }} />
              <h3 style={{ margin: 0 }}>Transaction Ledger</h3>
            </div>

            <DataTable
              columns={ledgerColumns}
              data={ledger}
              loading={ledgerLoading && ledger.length === 0}
              emptyMessage="No transactions yet."
            />

            {hasMoreLedger && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                <Button
                  variant="secondary"
                  onClick={() => fetchLedger(ledgerCursor)}
                  disabled={ledgerLoading}
                >
                  {ledgerLoading ? 'Loading…' : 'Load More'}
                </Button>
              </div>
            )}
          </>
        )}

        {showDeposit && <DepositModal onClose={() => setShowDeposit(false)} onDeposit={handleDeposit} />}
        {showCheckout && <CheckoutModal onClose={() => setShowCheckout(false)} onCheckout={handleCheckout} />}
      </div>
    </DashboardLayout>
  );
}
